import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BreadcrumbContextValue {
  customTitle: string | null;
  setCustomTitle: (title: string | null) => void;
  customCrumbs: BreadcrumbItem[] | null;
  setCustomCrumbs: (crumbs: BreadcrumbItem[] | null) => void;
}

export const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  customTitle: null,
  setCustomTitle: () => {},
  customCrumbs: null,
  setCustomCrumbs: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [customTitle, setCustomTitle] = useState<string | null>(null);
  const [customCrumbs, setCustomCrumbs] = useState<BreadcrumbItem[] | null>(null);

  return (
    <BreadcrumbContext.Provider value={{ customTitle, setCustomTitle, customCrumbs, setCustomCrumbs }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/** Hook to set the active leaf title in the breadcrumbs (e.g. employee name, company name, partner name) */
export function useBreadcrumbTitle(title: string | null | undefined) {
  const { setCustomTitle } = useContext(BreadcrumbContext);

  useEffect(() => {
    if (title) {
      setCustomTitle(title);
    }
    return () => {
      setCustomTitle(null);
    };
  }, [title, setCustomTitle]);
}

/** Hook to set full custom breadcrumbs for complex multi-level pages */
export function useBreadcrumbs(crumbs: BreadcrumbItem[] | null | undefined) {
  const { setCustomCrumbs } = useContext(BreadcrumbContext);

  useEffect(() => {
    if (crumbs) {
      setCustomCrumbs(crumbs);
    }
    return () => {
      setCustomCrumbs(null);
    };
  }, [crumbs, setCustomCrumbs]);
}
